import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../../constants/api.constants';
import { getAuthHeaders, refreshToken } from '../auth/Auth';

/**
 * Standard response format for API calls
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  timestamp: string;
}

/**
 * Extended axios request config with additional options
 */
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  requiresAuth?: boolean;
}

/**
 * Creates a configured axios instance with auth interceptors
 */
export const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    // Explicitly set adapter to 'fetch' for consistent behavior with axios 1.10.0+
    adapter: 'fetch',
  });

  // Add request interceptor to include auth headers
  instance.interceptors.request.use(
    async (config) => {
      // Skip auth for endpoints that don't require it
      /*
      if (config.requiresAuth === false) {
        return config;
      }
      */

      // Get auth headers from Auth module
      const headers = await getAuthHeaders();

      // Add headers to request
      Object.keys(headers).forEach(key => {
        config.headers[key] = headers[key];
      });

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Handle token refresh on 401 errors
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // If token expired and we haven't tried to refresh it yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          // Attempt to refresh the token
          const refreshed = await refreshToken();
          
          if (refreshed) {
            // Get updated auth headers
            const headers = await getAuthHeaders();
            
            // Update request headers
            Object.keys(headers).forEach(key => {
              originalRequest.headers[key] = headers[key];
            });
            
            // Retry the request using the same instance
            return instance(originalRequest);
          }
        } catch (refreshError) {
          // Let the error propagate if refresh fails
          return Promise.reject(refreshError);
        }
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// Create a singleton instance
export const apiClient = createApiClient();

// Create a raw axios instance without auth headers (for S3 uploads)
export const rawAxios = axios.create({
  timeout: 30000,
});

/**
 * Helper function to create a standardized API response
 */
export const createApiResponse = <T>(
  success: boolean,
  data?: T,
  message?: string
): ApiResponse<T> => ({
  success,
  data,
  message,
  timestamp: new Date().toISOString(),
});

/**
 * Helper for error handling
 */
export const handleApiError = <T>(error: unknown): ApiResponse<T> => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    console.error('API Error:', axiosError);
    
    if (axiosError.response) {
      // Server responded with an error
      const errorMessage = 
        typeof axiosError.response.data === 'object' && axiosError.response.data
          ? (axiosError.response.data as any).message 
          : 'Server error';
          
      return createApiResponse<T>(false, undefined, errorMessage);
    } else if (axiosError.request) {
      // Request was made but no response
      return createApiResponse<T>(false, undefined, 'No response from server');
    }
  }
  
  // Generic error fallback
  return createApiResponse<T>(
    false, 
    undefined, 
    error instanceof Error ? error.message : 'Unknown error occurred'
  );
};

/**
 * Generic API request function with type safety
 */
export const createApiRequest = <T>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  endpoint: string,
  config?: ExtendedAxiosRequestConfig
) => {
  return async (data?: any): Promise<T> => {
    try {
      let response: AxiosResponse;
      
      // Execute appropriate request based on method
      switch (method) {
        case 'get':
          response = await apiClient.get(endpoint, { ...config, params: data });
          break;
        case 'delete':
          response = await apiClient.delete(endpoint, { ...config, params: data });
          break;
        case 'post':
          response = await apiClient.post(endpoint, data, config);
          break;
        case 'put':
          response = await apiClient.put(endpoint, data, config);
          break;
        case 'patch':
          response = await apiClient.patch(endpoint, data, config);
          break;
      }

      return response.data as T;
    } catch (error) {
      // Let errors propagate naturally - calling code can handle with try/catch
      throw error;
    }
  };
};

export default apiClient;